<template>
  <main>
    <!-- Skeleton -->
    <div v-if="pending && !productData" class="mb-8 mt-10 f-col items-center container space-y-3">
      <div class="h-5 w-53 skeleton lg:h-6 lg:w-61 md:h-4" />
      <div class="skeleton lg:h-4 lg:w-61" />
    </div>
    <div v-else>
      <cms-section v-if="visibleESpots?.items.length" class="py-12 <md:py-8" :section="visibleESpots" />
      <div class="mb-8 mt-10 container">
        <vf-notification v-if="error" class="mb-4" :dismissible="false" type="error">
          {{ error.message }}
        </vf-notification>
        <h1 :class="brandClasses.title">
          {{ (isCmsTitle ? page?.title : productData?.pageHeaderData?.heading) || slug }}
        </h1>
      </div>
    </div>
    <product-list
      :grid-size-selector="$feature.showGridSizeSelectorOnSRP"
      :list="productData!"
      :next-page-loading="pending && infinityLoad"
      :pickup-filters="$feature.showPickupFiltersOnSRP"
      :products-loading="pending && !infinityLoad"
      :side-filters="$feature.showSideFiltersOnSRP"
      @load-more="loadMore"
    />
    <cms-section
      v-if="!pending && page?.sections?.['lazy-above-footer']?.items"
      class="py-12 <md:py-8"
      lazy-media
      page-context
      :section="page?.sections?.['lazy-above-footer']"
    />
  </main>
</template>

<script lang="ts" setup>
import type { PageContent } from '#types/page'

const props = defineProps<{
  pageType: string
}>()

const { $feature, $gtm } = useNuxtApp()
const { defaultSort } = useAppConfig().pages.plp
const { productsPerPage } = useAppConfig().components.product.list
const { brandClasses, isCmsTitle } = useAppConfig().components.thematic
const { getPage, getSeoMetadata } = useCms()
const { params } = useRoute()

definePageMeta({
  key: ({ query: { q } }) => q as string
})

const slug = ref(params.slug.toString().replaceAll('-', ' '))
const page = ref<PageContent>()

try {
  page.value = await getPage(`${props.pageType}/${params.slug}`)

  if (page.value) {
    const { metadata, link } = getSeoMetadata(page.value)
    useSeoMeta(metadata)
    useHead(() => ({ link }))
  }
}
catch {
  navigateTo(`/search?q=${slug.value}`, { replace: true })
}

const event = useRequestEvent()
const sort = useRouteQuery('sort', defaultSort)
const storeFilter = $feature.showPickupFiltersOnSRP ? useRouteQuery<string | undefined>('storeFilter') : undefined
const filters = useRouteQuery<string>('filters', '')
const filtersQuery = computed(() => getFiltersQueryString(filters.value))
const query = reactive({
  start: 0,
  count: productsPerPage,
  q: slug.value,
  sort: sort.value,
  filters: filtersQuery.value,
  locale: useLocale(),
  storeFilter: toValue(storeFilter),
  request_type: 'thematic',
})

const infinityLoad = ref(false)
const { data: productData, pending, error } = useApi().products.search(query, {
  key: slug.value,
  transform: (input) => ({
    ...input,
    filters: getUrlSafeFilters(input.filters),
    products: infinityLoad.value
      ? [
          ...(productData.value?.products || []),
          ...input.products
        ]
      : input.products,
  }),
  onResponseError: ({ response }) => {
    if (event) setResponseStatus(event, response.status, response.statusText)
  }
})

const visibleESpots = computed(() => productData?.value?.total
  ? page.value?.sections?.['above-grid']
  : page.value?.sections?.['above-grid-no-results']
)

const loadMore = () => {
  $gtm.push('plpPage.onViewMore')
  query.start += query.count
  infinityLoad.value = true
}

onBeforeUnmount (() => productData.value = null)

watch(() => [slug.value, sort.value, storeFilter?.value, filters.value], (newValue, oldValue) => {
  if (newValue[0] !== oldValue?.[0]) return
  if (newValue.every((v, i) => v === oldValue?.[i])) return
  infinityLoad.value = false
  query.start = 0
  query.sort = sort.value
  query.filters = filtersQuery.value
  query.storeFilter = toValue(storeFilter)
})
</script>
